import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "gantt-container"
}
const _hoisted_2 = { class: "gantt-layout d-flex" }
const _hoisted_3 = { class: "group-name" }
const _hoisted_4 = { class: "time-label" }
const _hoisted_5 = { class: "gantt-chart" }
const _hoisted_6 = { class: "grid-lines" }
const _hoisted_7 = { class: "gantt-bars" }
const _hoisted_8 = ["onClick", "onMouseenter"]
const _hoisted_9 = { class: "bar-content" }
const _hoisted_10 = { class: "bar-id" }
const _hoisted_11 = { class: "bar-title" }
const _hoisted_12 = {
  key: 1,
  class: "mobile-view"
}
const _hoisted_13 = { class: "mobile-header pa-4" }
const _hoisted_14 = { class: "legend-row d-flex flex-wrap ga-3 mb-4" }
const _hoisted_15 = { class: "text-caption font-weight-bold" }
const _hoisted_16 = { class: "mobile-timeline-list pa-4" }
const _hoisted_17 = { class: "timeline-flow" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "time-badge mb-2" }
const _hoisted_20 = { class: "d-flex justify-space-between align-center mb-2" }
const _hoisted_21 = { class: "d-flex align-center" }
const _hoisted_22 = { class: "duration-info text-caption" }
const _hoisted_23 = { class: "task-title text-body-1 font-weight-bold mb-2" }
const _hoisted_24 = {
  key: 0,
  class: "task-description text-body-2 mb-3"
}
const _hoisted_25 = { class: "progress-section" }
const _hoisted_26 = { class: "progress-bar" }
const _hoisted_27 = { class: "text-caption mt-1 text-center" }
const _hoisted_28 = {
  key: 0,
  class: "timeline-connector"
}
const _hoisted_29 = { class: "d-flex justify-space-between mb-1" }
const _hoisted_30 = { class: "text-body-2 font-weight-bold" }
const _hoisted_31 = { class: "d-flex justify-space-between mb-1" }
const _hoisted_32 = { class: "text-body-2 font-weight-bold" }
const _hoisted_33 = { class: "d-flex justify-space-between" }
const _hoisted_34 = {
  key: 0,
  class: "text-body-2 font-weight-bold"
}
const _hoisted_35 = {
  key: 1,
  class: "text-body-2 font-weight-bold"
}
const _hoisted_36 = { class: "tooltip-header" }
const _hoisted_37 = { class: "tooltip-time" }
const _hoisted_38 = { class: "tooltip-title" }
const _hoisted_39 = {
  key: 0,
  class: "tooltip-description"
}

import {computed, nextTick, onMounted, ref, toRef} from 'vue'
import {useDisplay} from 'vuetify'
import {TimelineItem, TimelineRow} from "@/types/VueScheduler";
import {Task, Group} from "@/types/Order";

interface ScheduleData {
  data: TimelineItem[]
  min: string
  max: string
}

interface Props {
  scheduleData: ScheduleData
  rows: TimelineRow[] | Group[]
  focused?: Task
}


const groupsPanelWidth = 100
const timeHeaderHeight = 60
const groupRowHeight = 80
const minTimeSlotWidth = 50

export default /*@__PURE__*/_defineComponent({
  __name: 'UniversalScheduler',
  props: {
    scheduleData: {},
    rows: {},
    focused: {}
  },
  emits: ["itemClick"],
  setup(__props: any, { emit: __emit }) {

const props = __props

//console.log("focused", props.focused)

const emit = __emit

const {mobile} = useDisplay()

// Reactive refs
const mouseX = ref(0);
const mouseY = ref(0);
const viewMode = ref<'desktop' | 'mobile'>('desktop')
const timelinePanel = ref<HTMLElement>()
const tooltipEl = ref<HTMLElement>()
const tooltip = ref<{
  show: boolean
  item: TimelineItem | null
  style: Record<string, any>
}>({
  show: false,
  item: null,
  style: {}
})

// Layout Constants
const barHeight = groupRowHeight - 10

// Computed properties
const isMobileView = computed(() => {
  return viewMode.value === 'mobile' || mobile.value
})

const groups = toRef(props.rows)

const sortedTimelineItems = computed(() => {
  return [...props.scheduleData.data].sort((a, b) => {
    return new Date(a.start).getTime() - new Date(b.start).getTime()
  })
})

const timeRange = computed(() => {
  if (!props.scheduleData.min || !props.scheduleData.max) return []

  const start = new Date(props.scheduleData.min)
  const end = new Date(props.scheduleData.max)
  const times: Date[] = []

  const current = new Date(start)
  current.setMinutes(0, 0, 0)

  while (current <= end) {
    times.push(new Date(current))
    current.setMinutes(current.getMinutes() + 30)
  }

  return times
})

const availableWidth = computed(() => {
  /*if (!timelinePanel.value) return 800
  return timelinePanel.value.clientWidth || 800*/
  return 1200 - minTimeSlotWidth;
})


const timeSlotWidth = computed(() => {
  if (timeRange.value.length === 0) return minTimeSlotWidth
  const calculatedWidth = availableWidth.value / timeRange.value.length
  return Math.max(calculatedWidth, minTimeSlotWidth)
})

const timeSlots = computed(() => {
  return timeRange.value.map(time => ({
    timestamp: time.getTime(),
    label: time.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }),
    width: timeSlotWidth.value
  }))
})

// Methods
const getItemsForGroup = (groupId: number): TimelineItem[] => {
  return props.scheduleData.data.filter(item => item.row === groupId)
}

const getGroupName = (rowId: number): string => {
  const group = groups.value.find(g => g.id === rowId)
  return group?.displayName || `Gruppe ${rowId}`
}

const getGroupColor = (rowId: number): string => {
  const group = groups.value.find(g => g.id === rowId)
  return group?.color || 'grey'
}

const getBarStyle = (item: TimelineItem): Record<string, string> => {
  if (!props.scheduleData.min) return {}

  const timelineStart = new Date(props.scheduleData.min)
  const itemStart = new Date(item.start)
  const itemEnd = new Date(item.end)

  const startOffsetHours = (itemStart.getTime() - timelineStart.getTime()) / (1000 * 60 * 60)
  const durationHours = (itemEnd.getTime() - itemStart.getTime()) / (1000 * 60 * 60)

  const left = startOffsetHours * 2 * timeSlotWidth.value
  //const width = (durationHours / totalDurationHours.value) * availableWidth.value
  const width = durationHours * 2 * timeSlotWidth.value

  const isHighlighted = item.meta.id == props.focused?.id && item.meta.orderId == props.focused?.order_id;

  return {
    position: 'absolute',
    left: `${left}px`,
    width: `${Math.max(width - 2, 50)}px`,
    height: `${barHeight}px`,
    top: `${(groupRowHeight - barHeight) / 2}px`,
    borderRadius: '6px',
    cursor: 'pointer',
  }
}

const formatTimeRange = (start: string, end: string): string => {
  const startTime = new Date(start).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })
  const endTime = new Date(end).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })
  return `${startTime} - ${endTime}`
}

const formatDuration = (start: string, end: string): string => {
  const startTime = new Date(start)
  const endTime = new Date(end)
  const durationMs = endTime.getTime() - startTime.getTime()
  const hours = Math.floor(durationMs / (1000 * 60 * 60))
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
  }
  return `${minutes}min`
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const getDurationPercentage = (item: TimelineItem): number => {
  const itemDuration = new Date(item.end).getTime() - new Date(item.start).getTime()
  const totalDuration = new Date(props.scheduleData.max).getTime() - new Date(props.scheduleData.min).getTime()
  return Math.min((itemDuration / totalDuration) * 100, 100)
}

const onItemClick = (item: TimelineItem) => {
  emit('itemClick', item)
}

const showTooltip = (event: MouseEvent, item: TimelineItem) => {
  if (isMobileView.value) return

  tooltip.value.item = item
  tooltip.value.show = true
}

const hideTooltip = () => {
  tooltip.value.show = false
  tooltip.value.item = null
}

function updateMouse(e: MouseEvent) {
  mouseX.value = e.clientX;
  mouseY.value = e.clientY;
  if (tooltip.value.show) {
    nextTick(() => {
      tooltip.value.style = {
        position: 'fixed',
        left: `${mouseX.value + 10}px`,
        top: `${mouseY.value - 10}px`,
        transform: 'translateY(-100%)',
        zIndex: 9999
      }
    })
  }
}

// Lifecycle
onMounted(() => {
  if (mobile.value) {
    viewMode.value = 'mobile'
  }
  window.addEventListener('mousemove', updateMouse);
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!isMobileView.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", {
              class: "groups-panel",
              style: _normalizeStyle({ width: groupsPanelWidth + 'px' })
            }, [
              _createElementVNode("div", {
                class: "time-header-placeholder",
                style: _normalizeStyle({ height: timeHeaderHeight + 'px' })
              }, null, 4),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: group.id,
                  class: "group-row",
                  style: _normalizeStyle({ height: groupRowHeight + 'px' })
                }, [
                  _createElementVNode("div", _hoisted_3, _toDisplayString(group.displayName), 1)
                ], 4))
              }), 128))
            ], 4),
            _createElementVNode("div", {
              class: "timeline-panel",
              ref_key: "timelinePanel",
              ref: timelinePanel
            }, [
              _createElementVNode("div", {
                class: "time-header",
                style: _normalizeStyle({ height: timeHeaderHeight + 'px' })
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timeSlots.value, (timeSlot) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: timeSlot.timestamp,
                    class: "time-slot",
                    style: _normalizeStyle({
                width: timeSlot.width + 'px',
                minWidth: minTimeSlotWidth + 'px'
              })
                  }, [
                    _createElementVNode("div", _hoisted_4, _toDisplayString(timeSlot.label), 1)
                  ], 4))
                }), 128))
              ], 4),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: group.id,
                    class: "gantt-row",
                    style: _normalizeStyle({ height: groupRowHeight + 'px' })
                  }, [
                    _createElementVNode("div", _hoisted_6, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timeSlots.value, (timeSlot) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `grid-${group.id}-${timeSlot.timestamp}`,
                          class: "grid-line",
                          style: _normalizeStyle({
                    width: timeSlot.width + 'px',
                    minWidth: minTimeSlotWidth + 'px',
                    borderBottom: '1px solid #f0f0f0'
                  })
                        }, null, 4))
                      }), 128))
                    ]),
                    _createElementVNode("div", _hoisted_7, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getItemsForGroup(group.id), (item) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `bar-${item.row}-${item.start}-${item.text}`,
                          class: _normalizeClass(["gantt-bar", `${item.color} ${(item.meta.id == props.focused?.id && item.meta.parent_id == props.focused?.order_id) ? 'highlightedBar' : ''}`]),
                          style: _normalizeStyle(getBarStyle(item)),
                          onClick: ($event: any) => (onItemClick(item)),
                          onMouseenter: ($event: any) => (showTooltip($event, item)),
                          onMouseleave: hideTooltip
                        }, [
                          _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(item.text), 1),
                            _createElementVNode("span", _hoisted_11, _toDisplayString(item.meta?.title || ''), 1)
                          ])
                        ], 46, _hoisted_8))
                      }), 128))
                    ])
                  ], 4))
                }), 128))
              ])
            ], 512)
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "text-h6 mb-3" }, "Gruppen-Legende", -1)),
            _createElementVNode("div", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: group.id,
                  class: "legend-chip d-flex align-center pa-2",
                  style: _normalizeStyle({ backgroundColor: 'bg-surface', borderColor: group.colorcode })
                }, [
                  _createElementVNode("div", {
                    class: "legend-dot mr-2",
                    style: _normalizeStyle({ backgroundColor: group.colorcode })
                  }, null, 4),
                  _createElementVNode("span", _hoisted_15, _toDisplayString(group.displayName), 1)
                ], 4))
              }), 128))
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-h6 mb-4" }, "Aufgaben-Timeline", -1)),
            _createElementVNode("div", _hoisted_17, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedTimelineItems.value, (item, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: `timeline-${item.row}-${item.start}-${item.text}`,
                  class: "timeline-card mb-3",
                  onClick: ($event: any) => (onItemClick(item))
                }, [
                  _createElementVNode("div", _hoisted_19, [
                    _createVNode(_component_v_icon, {
                      size: "16",
                      class: "mr-1"
                    }, {
                      default: _withCtx(() => _cache[1] || (_cache[1] = [
                        _createTextVNode("mdi-clock")
                      ])),
                      _: 1,
                      __: [1]
                    }),
                    _createTextVNode(" " + _toDisplayString(formatTimeRange(item.start, item.end)), 1)
                  ]),
                  _createVNode(_component_v_card, {
                    class: _normalizeClass(["task-card elevation-2", `${item.color}`]),
                    onClick: _withModifiers(($event: any) => (onItemClick(item)), ["stop"])
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card_text, { class: "pa-3" }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_20, [
                            _createElementVNode("div", _hoisted_21, [
                              _createVNode(_component_v_chip, {
                                size: "small",
                                variant: "elevated",
                                class: "task-id-chip mr-2"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(item.text), 1)
                                ]),
                                _: 2
                              }, 1024),
                              _createVNode(_component_v_chip, {
                                color: getGroupColor(item.row),
                                size: "x-small",
                                variant: "flat",
                                class: "group-chip"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(getGroupName(item.row)), 1)
                                ]),
                                _: 2
                              }, 1032, ["color"])
                            ]),
                            _createElementVNode("div", _hoisted_22, _toDisplayString(formatDuration(item.start, item.end)), 1)
                          ]),
                          _createElementVNode("div", _hoisted_23, _toDisplayString(item.meta?.title || 'Unbenannte Aufgabe'), 1),
                          (item.meta?.description)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(item.meta.description), 1))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_25, [
                            _createElementVNode("div", _hoisted_26, [
                              _createElementVNode("div", {
                                class: "progress-fill",
                                style: _normalizeStyle({
                        width: getDurationPercentage(item) + '%'
                      })
                              }, null, 4)
                            ]),
                            _createElementVNode("div", _hoisted_27, _toDisplayString(Math.round(getDurationPercentage(item))) + "% der Gesamtzeit ", 1)
                          ])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1032, ["class", "onClick"]),
                  (index < sortedTimelineItems.value.length - 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
                        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "connector-line" }, null, -1)),
                        _createVNode(_component_v_icon, {
                          size: "12",
                          class: "connector-arrow"
                        }, {
                          default: _withCtx(() => _cache[2] || (_cache[2] = [
                            _createTextVNode("mdi-arrow-down")
                          ])),
                          _: 1,
                          __: [2]
                        })
                      ]))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_18))
              }), 128))
            ]),
            _createVNode(_component_v_card, {
              class: "summary-card mt-4",
              variant: "outlined"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_text, { class: "pa-3" }, {
                  default: _withCtx(() => [
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-subtitle-2 mb-2 font-weight-bold" }, "Zusammenfassung", -1)),
                    _createElementVNode("div", _hoisted_29, [
                      _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-body-2" }, "Gesamtdauer:", -1)),
                      _createElementVNode("span", _hoisted_30, _toDisplayString(formatDuration(props.scheduleData.min, props.scheduleData.max)), 1)
                    ]),
                    _createElementVNode("div", _hoisted_31, [
                      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-body-2" }, "Anzahl Aufgaben:", -1)),
                      _createElementVNode("span", _hoisted_32, _toDisplayString(sortedTimelineItems.value.length), 1)
                    ]),
                    _createElementVNode("div", _hoisted_33, [
                      _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-body-2" }, "Zeitraum:", -1)),
                      (formatDate(props.scheduleData.min) == formatDate(props.scheduleData.max))
                        ? (_openBlock(), _createElementBlock("span", _hoisted_34, _toDisplayString(formatDate(props.scheduleData.min)), 1))
                        : (_openBlock(), _createElementBlock("span", _hoisted_35, _toDisplayString(formatDate(props.scheduleData.min)) + " - " + _toDisplayString(formatDate(props.scheduleData.max)), 1))
                    ])
                  ]),
                  _: 1,
                  __: [7]
                })
              ]),
              _: 1
            })
          ])
        ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (tooltip.value.show)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "tooltipEl",
            ref: tooltipEl,
            class: "gantt-tooltip",
            style: _normalizeStyle(tooltip.value.style)
          }, [
            _createElementVNode("div", _hoisted_36, [
              _createElementVNode("span", {
                class: _normalizeClass(`tooltip-id ${tooltip.value.item?.color}`)
              }, _toDisplayString(tooltip.value.item?.text), 3),
              _createElementVNode("span", _hoisted_37, _toDisplayString(tooltip.value.item ? formatTimeRange(tooltip.value.item.start, tooltip.value.item.end) : ''), 1)
            ]),
            _createElementVNode("div", _hoisted_38, _toDisplayString(tooltip.value.item?.meta?.title || 'Kein Titel'), 1),
            (tooltip.value.item?.meta?.description)
              ? (_openBlock(), _createElementBlock("div", _hoisted_39, _toDisplayString(tooltip.value.item.meta.description), 1))
              : _createCommentVNode("", true)
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})